/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { forwardRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { couponService, walletService } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import type { SavedPaymentMethod, WalletSummary } from '../../types';
import { loadRazorpayScript } from '../../utils/razorpay';
import { Button, Tag } from '../console/ui';
import { CardIc } from '../console/icons';
import BillingModal, { Notice } from './BillingModal';
import type { CouponValidationResult } from './billingUtils';
import { useInvalidateWallet } from './useBilling';

// ── Saved cards + "add card" (Razorpay recurring mandate) ────────────────

export const SavedCardsCard: React.FC<{
  wallet?: WalletSummary;
  cards: SavedPaymentMethod[];
  isLoading: boolean;
  refetch: () => void;
  mandateOpen: boolean;
  setMandateOpen: (b: boolean) => void;
}> = ({ wallet, cards, isLoading, refetch, mandateOpen, setMandateOpen }) => {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => !!s.user?.is_platform_admin);

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => walletService.setDefaultPaymentMethod(id),
    onSuccess: () => {
      refetch();
      toast.success('Default payment method updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => walletService.deletePaymentMethod(id),
    onSuccess: () => {
      refetch();
      toast.success('Payment method removed');
    },
  });

  const mandateMutation = useMutation({
    mutationFn: () => walletService.initiateMandateRegistration(),
    onSuccess: async (response) => {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        return;
      }
      const options = {
        key: response.data.key,
        amount: response.data.amount,
        currency: response.data.currency,
        name: response.data.name,
        description: response.data.description,
        order_id: response.data.order_id,
        customer_id: response.data.customer_id,
        recurring: '1',
        handler: async (paymentResponse: any) => {
          try {
            await walletService.completeMandateRegistration(
              paymentResponse.razorpay_order_id,
              paymentResponse.razorpay_payment_id,
              paymentResponse.razorpay_signature,
            );
            toast.success('Card saved successfully for auto-pay!');
            refetch();
            setMandateOpen(false);
          } catch (error) {
            console.error('Failed to complete mandate registration:', error);
            toast.error('Failed to save card');
          }
        },
        theme: { color: '#F97316' },
      };
      try {
        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', (resp: any) => {
          console.error('Payment failed:', resp.error);
          toast.error(`Payment failed: ${resp.error.description}`);
        });
        razorpay.open();
      } catch (error) {
        console.error('Failed to open Razorpay:', error);
        toast.error('Failed to open payment gateway');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to initiate card registration';
      if (errorMessage.includes('postpaid') || errorMessage.includes('customer ID')) {
        setMandateOpen(false);
        if (isAdmin) {
          toast.error('Please upgrade to postpaid account first');
          navigate('/kyc');
        } else {
          toast.error('Postpaid account required. Please contact support to enable postpaid billing.');
        }
      } else {
        toast.error(errorMessage);
      }
    },
  });

  return (
    <section className="ps-card p-5 flex flex-col gap-3" aria-labelledby="cards">
      <h2 id="cards" className="m-0 text-base font-medium text-ps-text">
        Saved cards
      </h2>
      {isLoading ? (
        <div className="h-[54px] rounded-[10px] bg-ps-raised animate-pulse" />
      ) : cards.length === 0 ? (
        <div className="p-4 border border-dashed border-ps-strong rounded-[10px] text-ps-muted text-sm">No saved cards</div>
      ) : (
        <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
          {cards.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-3 p-3 border border-ps-border rounded-[10px]">
              <span className="text-ps-muted [&>svg]:w-5 [&>svg]:h-5">
                <CardIc />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ps-text truncate">
                  {m.card_network} •••• {m.card_last4}
                </div>
                <div className="text-xs text-ps-subtle truncate">
                  {[m.card_type, m.card_issuer].filter(Boolean).join(' · ')}
                </div>
              </div>
              {m.is_default ? (
                <Tag tone="ok">Default</Tag>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setDefaultMutation.mutate(m.id)}
                  disabled={setDefaultMutation.isPending}
                >
                  Set default
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => deleteMutation.mutate(m.id)}
                disabled={deleteMutation.isPending}
                aria-label={`Remove card ending ${m.card_last4}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button variant="secondary" className="self-start" onClick={() => setMandateOpen(true)}>
        Add card
      </Button>

      <BillingModal open={mandateOpen} title="Add card for auto-pay" onClose={() => setMandateOpen(false)}>
        {wallet?.account_type === 'prepaid' ? (
          <>
            <Notice>
              <b className="font-medium">Postpaid account required.</b>{' '}
              {isAdmin
                ? 'To save cards and enable auto-pay, complete KYC verification and upgrade your account to postpaid.'
                : 'To save cards and enable auto-pay, your account needs to be on postpaid billing. Please contact support to enable postpaid for your account.'}
            </Notice>
            {isAdmin && (
              <Button
                onClick={() => {
                  setMandateOpen(false);
                  navigate('/kyc');
                }}
              >
                Complete KYC &amp; upgrade
              </Button>
            )}
          </>
        ) : (
          <>
            <Notice tone="info">
              <b className="font-medium">RBI-compliant auto-pay.</b> A ₹1 authorization charge verifies your card and is
              refunded. The card is saved securely by Razorpay for future automatic payments.
            </Notice>
            <Button onClick={() => mandateMutation.mutate()} disabled={mandateMutation.isPending}>
              {mandateMutation.isPending ? 'Processing…' : 'Add card & authorize'}
            </Button>
            <p className="m-0 text-xs text-ps-subtle text-center">Secure payment powered by Razorpay</p>
          </>
        )}
      </BillingModal>
    </section>
  );
};

// ── Auto-pay ─────────────────────────────────────────────────────────────

export const AutoPayCard: React.FC<{ wallet?: WalletSummary; cards: SavedPaymentMethod[]; onAddCard: () => void }> = ({
  wallet,
  cards,
  onAddCard,
}) => {
  const qc = useQueryClient();
  const on = !!wallet?.auto_debit_enabled;
  const defaultCard = cards.find((c) => c.is_default) || cards[0];

  const enableMutation = useMutation({
    mutationFn: (paymentMethodId: string) => walletService.enableAutoDebit(paymentMethodId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wallet'] });
      toast.success('Auto-pay enabled');
    },
  });
  const disableMutation = useMutation({
    mutationFn: () => walletService.disableAutoDebit(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wallet'] });
      toast.success('Auto-pay disabled');
    },
  });

  return (
    <section className="ps-card p-5 flex flex-col gap-3" aria-labelledby="ap">
      <div className="flex justify-between items-center gap-3">
        <h2 id="ap" className="m-0 text-base font-medium text-ps-text">
          Auto-pay
        </h2>
        <Tag tone={on ? 'ok' : 'off'}>{on ? 'On' : 'Off'}</Tag>
      </div>
      {on && cards.length === 0 && (
        <Notice>Auto-pay needs a saved card to work. Add one above, or turn auto-pay off.</Notice>
      )}
      <p className="m-0 text-[13px] text-ps-muted leading-relaxed">
        Automatically pays invoices with your default card
        {defaultCard ? ` (${defaultCard.card_network} •••• ${defaultCard.card_last4})` : ''}.
      </p>
      <div className="flex justify-between text-sm">
        <span className="text-ps-muted">Account type</span>
        <Tag tone="info">{wallet?.account_type === 'postpaid' ? 'Postpaid' : 'Prepaid'}</Tag>
      </div>
      <div className="flex gap-2 flex-wrap">
        {on ? (
          <Button variant="secondary" onClick={() => disableMutation.mutate()} disabled={disableMutation.isPending}>
            {disableMutation.isPending ? 'Turning off…' : 'Turn auto-pay off'}
          </Button>
        ) : defaultCard ? (
          <Button
            variant="secondary"
            onClick={() => enableMutation.mutate(defaultCard.id)}
            disabled={enableMutation.isPending}
          >
            {enableMutation.isPending ? 'Turning on…' : 'Turn auto-pay on'}
          </Button>
        ) : (
          <Button variant="secondary" onClick={onAddCard}>
            Add a card to use auto-pay
          </Button>
        )}
      </div>
    </section>
  );
};

// ── Promo code ───────────────────────────────────────────────────────────

export const PromoCodeCard = forwardRef<HTMLInputElement, { onRefund: () => void }>(({ onRefund }, inputRef) => {
  const invalidate = useInvalidateWallet();
  const [code, setCode] = useState('');
  const [validation, setValidation] = useState<CouponValidationResult | null>(null);

  const validateMutation = useMutation({
    mutationFn: (c: string) => couponService.validate(c),
    onSuccess: (response) => {
      setValidation(response.data as CouponValidationResult);
      if (response.data.valid) toast.success('Coupon is valid!');
      else toast.error(response.data.message || 'Invalid coupon');
    },
    onError: (error: any) => {
      setValidation(null);
      toast.error(error.response?.data?.error || 'Failed to validate coupon');
    },
  });

  const redeemMutation = useMutation({
    mutationFn: (c: string) => couponService.redeem(c),
    onSuccess: (response) => {
      invalidate();
      setCode('');
      setValidation(null);
      const r = response.data;
      if (r.credits_awarded > 0) toast.success(`₹${r.credits_awarded} credits added to your wallet!`);
      else if (r.discount_applied > 0) toast.success(`Coupon redeemed! ₹${r.discount_applied} discount will be applied.`);
      else toast.success('Coupon redeemed successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to redeem coupon');
    },
  });

  const c = validation?.coupon;
  const headline =
    c?.type === 'credits'
      ? `₹${validation?.credit_amount} free credits`
      : c?.type === 'percentage'
        ? `${c.discount_value}% off`
        : `₹${validation?.discount_amount} off`;

  return (
    <section className="ps-card p-5 flex flex-col gap-2.5" aria-labelledby="cp">
      <h2 id="cp" className="m-0 text-base font-medium text-ps-text">
        Promo code
      </h2>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (code) validateMutation.mutate(code);
        }}
      >
        <label className="sr-only" htmlFor="promo-code">
          Promo code
        </label>
        <input
          id="promo-code"
          ref={inputRef}
          className="ps-input flex-1 font-mono uppercase"
          placeholder="Enter code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setValidation(null);
          }}
        />
        <Button type="submit" variant="secondary" disabled={!code || validateMutation.isPending}>
          {validateMutation.isPending ? 'Checking…' : 'Apply'}
        </Button>
      </form>

      {validation &&
        (validation.valid ? (
          <div className="p-3 rounded-md bg-ps-ok-bg flex flex-col gap-2">
            <span className="text-ps-ok text-sm font-medium">{headline}</span>
            {c?.description && <span className="text-ps-ok/80 text-[13px]">{c.description}</span>}
            <Button onClick={() => redeemMutation.mutate(code)} disabled={redeemMutation.isPending}>
              {redeemMutation.isPending
                ? 'Redeeming…'
                : c?.type === 'credits'
                  ? `Redeem ₹${validation.credit_amount} credits`
                  : 'Redeem coupon'}
            </Button>
            {c?.type !== 'credits' && (
              <span className="text-xs text-ps-ok/80">This discount applies to your next top-up or purchase.</span>
            )}
          </div>
        ) : (
          <Notice tone="err">{validation.message}</Notice>
        ))}

      <button type="button" onClick={onRefund} className="ps-link text-sm self-start min-h-[32px]">
        Request a refund
      </button>
    </section>
  );
});

PromoCodeCard.displayName = 'PromoCodeCard';
